import React, { useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { isMapAlive } from './mapGuard';
import type { FeatureCollection } from 'geojson';
import type { LayerId, LayerVisibility } from '../../../types/mapEngine';

interface Props {
  map: mapboxgl.Map;
  congestionData: FeatureCollection | null;
  arrivalsData: FeatureCollection | null;
  departuresData: FeatureCollection | null;
  layerVisibility: LayerVisibility;
}

const EMPTY_FC: FeatureCollection = { type: 'FeatureCollection', features: [] };

const TCF_COLOR_RAMP: mapboxgl.Expression = [
  'interpolate',
  ['linear'],
  ['get', 'tcf'],
  0,   '#1565C0',
  0.3, '#388E3C',
  0.6, '#F9A825',
  0.8, '#E64A19',
  1,   '#B71C1C',
];

const ARRIVALS_COLOR_RAMP: mapboxgl.Expression = [
  'interpolate',
  ['linear'],
  ['get', 'tcf'],
  0,   '#FFE0B2',
  0.5, '#FF5722',
  1,   '#BF360C',
];

const DEPARTURES_COLOR_RAMP: mapboxgl.Expression = [
  'interpolate',
  ['linear'],
  ['get', 'tcf'],
  0,   '#E8F5E9',
  0.5, '#4CAF50',
  1,   '#1B5E20',
];

const VISIBILITY_MAP: Partial<Record<LayerId, string[]>> = {
  'heatmap-congestion':  ['me-congestion-line'],
  'heatmap-arrivals':    ['me-arrivals-line'],
  'heatmap-departures':  ['me-departures-line'],
};

const CongestionLayer: React.FC<Props> = ({
  map,
  congestionData,
  arrivalsData,
  departuresData,
  layerVisibility,
}) => {
  // Effect 1: add sources + layers on mount
  useEffect(() => {
    if (!map.getSource('me-congestion')) {
      map.addSource('me-congestion', { type: 'geojson', data: congestionData ?? EMPTY_FC });
    }
    if (!map.getSource('me-arrivals')) {
      map.addSource('me-arrivals', { type: 'geojson', data: arrivalsData ?? EMPTY_FC });
    }
    if (!map.getSource('me-departures')) {
      map.addSource('me-departures', { type: 'geojson', data: departuresData ?? EMPTY_FC });
    }

    if (!map.getLayer('me-congestion-line')) {
      map.addLayer({
        id: 'me-congestion-line',
        type: 'line',
        source: 'me-congestion',
        paint: {
          'line-width': 2.5,
          'line-color': TCF_COLOR_RAMP,
        },
      });
    }

    if (!map.getLayer('me-arrivals-line')) {
      map.addLayer({
        id: 'me-arrivals-line',
        type: 'line',
        source: 'me-arrivals',
        paint: {
          'line-width': 2.5,
          'line-color': ARRIVALS_COLOR_RAMP,
        },
      });
    }

    if (!map.getLayer('me-departures-line')) {
      map.addLayer({
        id: 'me-departures-line',
        type: 'line',
        source: 'me-departures',
        paint: {
          'line-width': 2.5,
          'line-color': DEPARTURES_COLOR_RAMP,
        },
      });
    }

    return () => {
      if (!isMapAlive(map)) return;
      ['me-departures-line', 'me-arrivals-line', 'me-congestion-line'].forEach((id) => {
        if (map.getLayer(id)) map.removeLayer(id);
      });
      ['me-departures', 'me-arrivals', 'me-congestion'].forEach((id) => {
        if (map.getSource(id)) map.removeSource(id);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Effect 2: sync source data
  useEffect(() => {
    const updates: Array<[string, FeatureCollection | null]> = [
      ['me-congestion',  congestionData],
      ['me-arrivals',    arrivalsData],
      ['me-departures',  departuresData],
    ];
    updates.forEach(([sourceId, data]) => {
      const src = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
      if (src) src.setData(data ?? EMPTY_FC);
    });
  }, [map, congestionData, arrivalsData, departuresData]);

  // Effect 3: visibility
  useEffect(() => {
    (Object.keys(VISIBILITY_MAP) as LayerId[]).forEach((key) => {
      const vis = layerVisibility[key];
      if (vis === undefined) return;
      (VISIBILITY_MAP[key] ?? []).forEach((layerId) => {
        if (map.getLayer(layerId)) {
          map.setLayoutProperty(layerId, 'visibility', vis ? 'visible' : 'none');
        }
      });
    });
  }, [map, layerVisibility]);

  return null;
};

export default CongestionLayer;
